import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function ConstantiaPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Constantia Painters Cape Town | Premium Wine Estate Painting Services | Luxury Property Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium painting services for Constantia wine estates and luxury properties. Expert interior & exterior painting for prestigious Constantia homes with specialized heritage property techniques.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Constantia painters, wine estate painting, luxury property painting, Constantia homes, Cape Town premium painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/constantia`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Constantia Painters - Premium Wine Estate Painting Services",
      "description": "Premium painting services for Constantia wine estates and luxury properties with specialized heritage techniques.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/constantia`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Constantia",
        "description": "Premium painting services for Constantia's prestigious wine estates and luxury properties.",
        "areaServed": "Constantia, Cape Town",
        "serviceType": "Wine Estate and Luxury Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Constantia')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Wine Estate Painting',
      description: 'Specialized painting services for Constantia\'s historic wine estates, including heritage buildings, tasting rooms, and luxury residences with period-appropriate techniques.',
      price: 'From R25,000',
      features: ['Heritage restoration', 'Period-appropriate colors', 'Wine cellar protection', 'Tasting room finishes', 'Estate coordination']
    },
    {
      icon: 'ri-building-line',
      title: 'Luxury Home Painting',
      description: 'Premium interior and exterior painting for Constantia\'s exclusive residential properties, featuring custom color schemes and high-end finishes.',
      price: 'From R18,000',
      features: ['Custom color consultation', 'Premium materials', 'Architectural detailing', 'Landscape integration', 'Quality assurance']
    },
    {
      icon: 'ri-palette-line',
      title: 'Heritage Restoration',
      description: 'Specialized restoration painting for Constantia\'s historic properties, using traditional techniques and authentic materials to preserve architectural integrity.',
      price: 'From R15,000',
      features: ['Historical accuracy', 'Traditional techniques', 'Authentic materials', 'Conservation methods', 'Heritage compliance']
    }
  ];

  const features = [
    {
      title: 'Wine Estate Expertise',
      description: 'Specialized knowledge in painting wine estates, including cellars, tasting rooms, and production facilities with appropriate protective coatings and finishes that complement the winemaking environment.'
    },
    {
      title: 'Heritage Property Specialists',
      description: 'Expert restoration of Constantia\'s historic properties using traditional techniques and period-appropriate materials while meeting modern protection standards.'
    },
    {
      title: 'Luxury Residential Focus',
      description: 'Premium painting services for Constantia\'s exclusive homes, featuring custom color consultation, high-end materials, and meticulous attention to architectural details.'
    },
    {
      title: 'Environmental Consideration',
      description: 'Eco-friendly painting solutions that respect Constantia\'s natural wine valley environment while providing superior protection and longevity.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Estate Consultation',
      description: 'Comprehensive property assessment including heritage considerations, wine estate requirements, and custom design planning for your Constantia property.'
    },
    {
      step: 2,
      title: 'Heritage Preparation',
      description: 'Careful surface preparation using appropriate techniques for historic properties, including specialized treatments for wine estate environments.'
    },
    {
      step: 3,
      title: 'Expert Application',
      description: 'Professional application using premium materials and traditional techniques where appropriate, ensuring results that honor Constantia\'s heritage.'
    },
    {
      step: 4,
      title: 'Quality Assurance',
      description: 'Thorough inspection and quality control ensuring every detail meets the high standards expected in Constantia\'s prestigious properties.'
    }
  ];

  const testimonials = [
    {
      name: 'James Wellington',
      property: 'Historic Wine Estate',
      rating: 5,
      text: 'Outstanding work on our heritage wine estate. The team understood the historical significance and delivered results that perfectly complement our property\'s character. The attention to detail was exceptional.',
      image: 'https://public.readdy.ai/ai/img_res/137ba33067568add30aeaf88b10b2ed5.jpg'
    },
    {
      name: 'Sarah Mitchell',
      property: 'Luxury Constantia Home',
      rating: 5,
      text: 'Exceptional service for our Constantia home. The color consultation was excellent and the final result exceeded our expectations. Professional team with deep understanding of luxury properties.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20homeowner%20at%20luxury%20Constantia%20property%20with%20mountain%20views%2C%20satisfied%20client%20portrait%2C%20upscale%20residential%20setting&width=80&height=80&seq=constantia2&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Historic Wine Estate Restoration',
      description: 'Complete restoration of a 300-year-old wine estate including heritage buildings, tasting facilities, and residential quarters using period-appropriate techniques and materials.',
      image: 'https://readdy.ai/api/search-image?query=Historic%20wine%20estate%20in%20Constantia%20valley%20with%20Cape%20Dutch%20architecture%2C%20vineyard%20setting%2C%20heritage%20building%20restoration%2C%20professional%20painting%20work&width=400&height=300&seq=constantia3&orientation=landscape'
    },
    {
      title: 'Luxury Mountain Home',
      description: 'Premium painting project for an exclusive Constantia residence featuring custom color schemes, architectural detailing, and integration with the natural landscape.',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20home%20in%20Constantia%20with%20mountain%20backdrop%2C%20modern%20architecture%2C%20vineyard%20views%2C%20professional%20exterior%20painting&width=400&height=300&seq=constantia4&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20wine%20estate%20in%20Constantia%20valley%20with%20vineyard%20backdrop%2C%20Cape%20Dutch%20architecture%2C%20painting%20equipment%2C%20heritage%20property%20restoration%2C%20sunny%20day&width=1920&height=1080&seq=constantia_hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Constantia
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Premium painting services for Constantia\'s wine estates and luxury properties. Expert craftsmanship for heritage buildings and exclusive homes with over 20 years of experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Constantia Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Premium Painting Services for Constantia
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Constantia stands as South Africa\'s oldest wine region and one of Cape Town\'s most prestigious suburbs, renowned for its historic wine estates, luxury properties, and stunning mountain settings. This exclusive area demands painting services that respect its rich heritage while meeting modern luxury standards.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our specialized team has extensive experience serving Constantia\'s unique property market, from 300-year-old wine estates to contemporary luxury homes. We understand the delicate balance required between preserving historical authenticity and providing modern protection and beauty.
              </p>
              <p className="text-lg text-gray-600">
                Whether working on heritage Cape Dutch architecture, modern wine facilities, or exclusive residential properties, our Constantia painting specialists deliver results that honor the area\'s distinguished character while ensuring long-lasting protection and beauty.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Constantia%20wine%20valley%20with%20historic%20estates%2C%20vineyard%20landscapes%2C%20Cape%20Dutch%20architecture%2C%20prestigious%20residential%20area%2C%20mountain%20backdrop&width=600&height=400&seq=constantia5&orientation=landscape"
                alt="Constantia Wine Valley"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Constantia Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Constantia\'s wine estates, heritage properties, and luxury homes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Constantia?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in wine estate and heritage property painting with premium materials and techniques.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Constantia Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A specialized approach developed for Constantia\'s unique wine estate and heritage property requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Constantia Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent painting projects across Constantia\'s wine estates and luxury properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Constantia property owners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready for Premium Painting in Constantia?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town\'s wine estate and heritage property painting specialists. Experience premium craftsmanship for your Constantia property.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
            <a
              href="mailto:Get a quote"
              className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}